"use client";

import {
  AlertCircle,
  Calendar,
  DollarSign,
  ShieldCheck,
  Swords,
  Users,
} from "lucide-react";
import dynamic from "next/dynamic";
import { translateUi } from "@/shared/lib/ui-translate";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useShallow } from "zustand/react/shallow";
import { Link } from "@/i18n/navigation";
import { useTournamentStore } from "@/entities/tournament";
import { useMyRegistration } from "@/entities/tournament/api/use-my-registration";
import { useFeatureFlagsState } from "@/hooks/useFeatureFlags";
import { api } from "@/shared/lib/api";

import type {
  Tournament,
  PrizeDistributionRowDto,
} from "@/entities/tournament/model/types";
import { getTournamentDisplayPrizeLabel } from "@/entities/tournament/lib/prize-display";
import { useUserProfile } from "@/hooks/useAuth";
import { canManageTournament } from "@/entities/tournament/lib/permissions";
import { getTournamentNextAction } from "@/entities/tournament/lib/tournament-lifecycle";
import { cn } from "@/shared/lib/utils";
import {
  MSK_TIME_ZONE,
  formatDateMsk,
  formatDateOnlyMsk,
  formatTimeOnlyMsk,
} from "@/shared/lib/msk-time";

/**
 * Подсказка к дате драфта: «сегодня» / «завтра» / «через N дней» / «прошёл».
 * Сравниваем по календарным дням в МСК — иначе «через 1 день» показывалось бы
 * и для события через 30 часов, и для завтрашнего утра.
 */
function formatDraftStartHint(iso: string): string {
  const startOfDayMsk = (d: Date) =>
    new Date(
      // eslint-disable-next-line no-restricted-syntax -- ключ сравнения дат, не текст: en-CA даёт YYYY-MM-DD в МСК
      d.toLocaleDateString("en-CA", { timeZone: MSK_TIME_ZONE }),
    ).getTime();
  const target = new Date(iso);
  const days = Math.round(
    (startOfDayMsk(target) - startOfDayMsk(new Date())) / 86400000,
  );
  if (days === 0) return translateUi("litTournamentDetailTabs.segodnya");
  if (days === 1) return translateUi("litTournamentDetailTabs.zavtra");
  if (days === -1) return translateUi("litTournamentDetailTabs.vchera");
  if (days < 0) return translateUi("litTournamentDetailTabs.dnNazad", { days: Math.abs(days) });
  return translateUi("litTournamentDetailTabs.cherezDney", { count: days });
}
import { formatMinRequiredRankRu } from "@/shared/lib/riot-rank";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/shared/ui/cards/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { Button } from "@/shared/ui/buttons/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { Progress } from "@/shared/ui/progress";
import { DraftRoom } from "@/widgets/draft/draft-room.dynamic";
import { DRAFT_ACTIONS_SLOT_ID } from "@/widgets/draft/draft-header";

import {
  TournamentRulebookOverviewBlock,
  useRulebookHasContent,
} from "@/features/tournament/rulebook-overview-block";
import { TournamentAboutModeratorsBlock } from "@/features/tournament/tournament-about-moderators-block";
import { TournamentRulesText } from "@/features/tournament/tournament-rules-text";
import { useTournamentDeadlineToast } from "@/features/tournament/use-tournament-deadline-toast";
import { TournamentParticipantsGrid } from "./tournament-participants-grid";
import { TournamentParticipantsTeamsGrid } from "./tournament-participants-teams-grid";
import { TournamentTempTeamsPanel } from "./tournament-temp-teams-panel";
import { TournamentOfficialStream } from "./tournament-official-stream";
import { TournamentStreamersTab } from "./tournament-streamers-tab";
import { ArenaPanel } from "./arena/arena-panel";
import { ArenaRostersPanel } from "./arena/arena-rosters-panel";
import { ArenaLeaderboard } from "./arena/arena-leaderboard";
import { WidgetBoundary } from "@/shared/lib/widget-boundary";

const TournamentBracketTab = dynamic(
  () =>
    import("./tabs/tournament-bracket-tab").then((m) => m.TournamentBracketTab),
  { ssr: false },
);
const TournamentResultsTab = dynamic(
  () =>
    import("./tabs/tournament-results-tab").then((m) => m.TournamentResultsTab),
  { ssr: false },
);
const TournamentScheduleTab = dynamic(
  () =>
    import("./tabs/tournament-schedule-tab").then(
      (m) => m.TournamentScheduleTab,
    ),
  { ssr: false },
);
const TournamentManagementTab = dynamic(
  () =>
    import("@/widgets/tournament-management/tournament-management-tab").then(
      (m) => m.TournamentManagementTab,
    ),
  { ssr: false },
);
const TournamentSettingsTab = dynamic(
  () =>
    import("@/widgets/tournament-management/tournament-settings-tab").then(
      (m) => m.TournamentSettingsTab,
    ),
  { ssr: false },
);
const TournamentAuditTab = dynamic(
  () =>
    import("@/widgets/tournament-management/tournament-audit-tab").then(
      (m) => m.TournamentAuditTab,
    ),
  { ssr: false },
);
const TournamentReviewsComplaintsTab = dynamic(
  () =>
    import("./tabs/tournament-reviews-complaints-tab").then(
      (m) => m.TournamentReviewsComplaintsTab,
    ),
  { ssr: false },
);
const TournamentPredictionsScreen = dynamic(
  () =>
    import("@/features/tournament-predictions").then(
      (m) => m.TournamentPredictionsScreen,
    ),
  { ssr: false },
);

// Палитра карточек призовых мест (из макета «Страница турнира.dc.html»):
// золото / серебро / бронза, дальше — нейтрально. Ключ — placeFrom.
type PrizeMedal = { border: string; bg: string; text: string };
const DEFAULT_PRIZE_MEDAL: PrizeMedal = {
  border: "var(--border)",
  bg: "var(--card)",
  text: "linear-gradient(180deg,var(--foreground),var(--muted-foreground))",
};
const PRIZE_MEDALS: Record<string, PrizeMedal> = {
  "1": {
    border: "color-mix(in srgb, #f59e0b 55%, var(--border))",
    bg: "linear-gradient(160deg, color-mix(in srgb, #f59e0b 20%, var(--card)), var(--card) 70%)",
    text: "linear-gradient(180deg,#f59e0b,#b45309)",
  },
  "2": {
    border: "color-mix(in srgb, #94a3b8 60%, var(--border))",
    bg: "linear-gradient(160deg, color-mix(in srgb, #94a3b8 22%, var(--card)), var(--card) 70%)",
    text: "linear-gradient(180deg,#94a3b8,#475569)",
  },
  "3": {
    border: "color-mix(in srgb, #ea580c 50%, var(--border))",
    bg: "linear-gradient(160deg, color-mix(in srgb, #ea580c 18%, var(--card)), var(--card) 70%)",
    text: "linear-gradient(180deg,#fb923c,#9a3412)",
  },
};

/**
 * Бюджет капитана в шапке списка участников.
 *
 * Показывает не только общую сумму, но и то, что стоимость САМОГО капитана из неё
 * вычитается: капитаны регулярно считают бюджет как «на четверых новичков», хотя
 * на деле их собственная цена уже съедает часть. Без этой оговорки первый же дорогой
 * капитан упирается в нехватку денег на последнем пике и считает это багом.
 */
function DraftBudgetBadge({ budget }: { budget?: number }) {
  const tUi = useTranslations("Ui");
  if (!budget || budget <= 0) return null;

  return (
    <div className="rounded-xl border border-border bg-muted/40 px-3.5 py-2 text-right">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {tUi("tournamentDetailTabs.byudzhetKomandy")}
      </div>
      <div className="text-xl font-bold leading-tight text-foreground">
        {budget}
      </div>
      <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
        {tUi("tournamentDetailTabs.vklyuchayaKapitanaEgoStoimost")}
        <br />
        {tUi("tournamentDetailTabs.vychitaetsyaIzEtoySummy")}
      </div>
    </div>
  );
}

interface TournamentDetailTabsProps {
  tournament: Tournament;
}

export const TournamentDetailTabs = ({
  tournament,
}: TournamentDetailTabsProps) => {
  const tUi = useTranslations("Ui");
  const tl = useTranslations("Tournaments");
  const { data: user } = useUserProfile();
  const fetchTournamentById = useTournamentStore(
    useShallow((state) => state.fetchTournamentById),
  );
  const searchParams = useSearchParams();
  const tournamentId = tournament.id ?? "";
  const isCompleted =
    tournament.status === "Completed" ||
    String(tournament.status ?? "")
      .toLowerCase()
      .includes("completed") ||
    String(tournament.backendStatus ?? "")
      .toLowerCase()
      .includes("completed");
  // Arena Random: пьедестал/итоги живут на вкладке «Лидерборд» (arena), а не в
  // generic «Итоги» (который у Арены пуст — нет bracket/results-данных).
  const isArenaRandom =
    tournament.type === "Arena" && tournament.arenaTeamMode === "Random";
  const completedDefaultTab = isArenaRandom ? "arena" : "results";
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get("tab");
    if (tab) return tab;
    return isCompleted ? completedDefaultTab : "overview";
  });

  // Явный выбор пользователя (клик по табу) — сильнее URL и сильнее любых
  // производных значений. `setTab` пишет адрес через history.replaceState;
  // Next патчит replaceState и синхронизирует useSearchParams, но делает это
  // через startTransition — то есть НА РЕНДЕР ПОЗЖЕ. В этом окне эффекты ниже
  // видят ещё СТАРЫЙ ?tab= и, сработав, откатывают выбор обратно. Отсюда два
  // маркера: «пользователь уже выбирал сам» и «какое значение ?tab= мы уже
  // отработали» (второе позволяет по-прежнему уважать НАСТОЯЩУЮ смену адреса,
  // например переход по внешней ссылке на ?tab=bracket на той же странице).
  const userPickedTabRef = useRef(false);
  const handledUrlTabRef = useRef<string | null>(searchParams.get("tab"));

  const { flags, isLoading: flagsLoading } = useFeatureFlagsState();
  // KAN-957: карточку «Правила» рисуем, только если в ней есть что показать.
  const isSolo1v1 =
    tournament.format === "SoloOneVsOne" || tournament.type === "SoloOneVsOne";
  // KAN-665: регламент турнира, который организатор пишет в настройках.
  const tournamentRulesText = (tournament.rules ?? "").trim();
  const rulebookState = useRulebookHasContent(
    tournamentId,
    !!flags.rulebook && !!tournamentId,
  );
  const showRulesCard =
    isSolo1v1 ||
    tournamentRulesText.length > 0 ||
    (!!flags.rulebook && (rulebookState.isLoading || rulebookState.hasContent));
  const isManager = canManageTournament(
    user?.id ?? undefined,
    user?.primaryPlatformRole ?? undefined,
    tournament,
  );
  // Стейт-машина Arena Random: составы редактируются ДО старта, баллы — пока турнир идёт.
  // KAN-147 #5: backendStatus задаётся только store-маппингом (сырой enum); из других
  // источников он undefined → фолбэк на tournament.status (для не-store турниров это
  // тоже сырой enum). AwaitingBracket = после старта → тоже «running», иначе панель
  // залипает в preview (reshuffle доступен, редактирование лидерборда заблокировано).
  const arenaBackendStatus = String(
    tournament.backendStatus ?? tournament.status ?? "",
  );
  const arenaRunning =
    arenaBackendStatus === "Ongoing" ||
    arenaBackendStatus === "InProgress" ||
    arenaBackendStatus === "AwaitingBracket";
  // KAN-935: тот же общий кэш, что и у блока кнопок (`tournament-actions`).
  // Раньше каждый спрашивал ручку сам — два одинаковых запроса на открытие.
  const { data: myRegistrationResponse } = useMyRegistration(tournamentId, {
    enabled: !!tournamentId && !!user?.id,
  });
  useTournamentDeadlineToast(
    tournamentId,
    tournament,
    myRegistrationResponse ?? undefined,
  );

  // Капитан драфта — ему в списке участников доступен личный шортлист (звёздочки).
  //
  // KAN-935: тянется только на вкладке участников. Единственный потребитель —
  // `isCaptain` у грида участников, а запрос до правки уходил на любой вкладке,
  // включая «Обзор», который открывается по умолчанию у всех.
  const { data: draftCaptains } = useQuery({
    queryKey: ["tournaments", tournamentId, "draft", "captains"],
    queryFn: async () => {
      const { data } = await api.get<{ slots?: { userId?: string | null }[] }>(
        `/tournaments/${tournamentId}/draft/captains`,
      );
      return data;
    },
    enabled:
      !!tournamentId &&
      !!user?.id &&
      tournament.format === "Draft" &&
      activeTab === "teams",
  });
  const isDraftCaptain = Boolean(
    user?.id &&
    (draftCaptains?.slots ?? []).some((sl) => sl?.userId === user.id),
  );

  const maxTeams = tournament.maxTeams ?? 0;
  const currentTeams = tournament.currentTeams ?? 0;
  const prizeDistribution = (
    tournament as { prizeDistribution?: PrizeDistributionRowDto[] }
  ).prizeDistribution;
  const prizeLabel = getTournamentDisplayPrizeLabel(
    tournament.prizePool,
    prizeDistribution,
  );
  const currencySymbol = tournament.currency === "USD" ? "$" : "₽";
  const displayPrizeText =
    prizeLabel.type === "prizes"
      ? [prizeLabel.prizeTypeLabel, prizeLabel.placesRange]
          .filter(Boolean)
          .join(", ")
      : `${currencySymbol}${prizeLabel.value.toLocaleString()}`;
  const startDate = tournament.startDate
    ? new Date(tournament.startDate)
    : null;
  const endDate = tournament.endDate ? new Date(tournament.endDate) : null;
  const tournamentFormat = tournament.format ?? "Classic";
  const minRequiredRankRu = formatMinRequiredRankRu(tournament.minRequiredRank);

  const selfHref = useMemo(() => {
    // slug: не входит в текущий Tournament (entities/tournament) — fallback
    // на случай старых закэшированных ответов с плоским полем.
    const slug = (tournament as unknown as Record<string, unknown>)["slug"] as
      string | undefined;
    const id = tournament.id ?? "";
    if (slug) return `/tournaments/${slug}`;
    if (id) return `/tournaments/${id}`;
    return null;
  }, [tournament]);

  const isArena = tournament.type === "Arena";
  // ВРЕМЕННО: таб «Квесты» (webview) только для текущего Arena-турнира «Baddie».
  // Привязан к конкретному id — больше нигде не показывается. Убрать после ивента.
  const QUESTS_TOURNAMENT_ID = "a20540c7-6049-408e-aabc-42dd882a595f";
  const isQuestTournament = (tournament.id ?? "") === QUESTS_TOURNAMENT_ID;
  type TabDef = { id: string; label: string };
  const tabs: TabDef[] = [
    ...(isCompleted && !isArenaRandom
      ? [{ id: "results", label: tl("tabs.results") }]
      : []),
    { id: "overview", label: tl("tabs.overview") },
    ...(tournament.format === "Draft"
      ? [{ id: "draft", label: tl("tabs.draft") }]
      : []),
    {
      id: "teams",
      label:
        tournament.format === "Draft" || tournament.format === "SoloOneVsOne"
          ? tl("tabs.participants")
          : tl("tabs.teams"),
    },
    ...(isArena
      ? tournament.arenaTeamMode === "Random"
        ? // Random: «Таблица» = лидерборд, осмысленна только после сборки команд.
          // До сборки прячем — иначе дублирует «Команды».
          tournament.arenaAssemblyState === "NotAssembled"
          ? []
          : [{ id: "arena", label: tl("tabs.leaderboard") }]
        : [{ id: "arena", label: tl("tabs.arena") }]
      : [{ id: "bracket", label: tl("tabs.bracket") }]),
    // Стримеры — только у драфт-турниров и только ПОСЛЕ драфта: до него команд
    // ещё нет, а вкладка группирует стримеров по командам.
    ...(tournament.format === "Draft" && tournament.isDraftCompleted
      ? [{ id: "streamers", label: tUi("litTournamentDetailTabs.strimery") }]
      : []),
    ...(isQuestTournament ? [{ id: "quests", label: tUi("litTournamentDetailTabs.kvesty") }] : []),
    ...(flags.predictions
      ? [{ id: "predictions", label: tl("tabs.predictions") }]
      : []),
    { id: "schedule", label: tl("tabs.schedule") },
    // Таб «Чемпионы» убран — статистика по чемпионам/пикам теперь в «Итогах»
    // (перформанс игроков + highlights). Чтобы не дублировать.
    // Диспуты/жалобы скрыты (MVP): вкладка вернётся вместе с фичей, код — в истории git.
    ...(isManager
      ? [
          { id: "management", label: tl("tabs.management") },
          // «Аудит» скрыт из панели табов по решению редизайна (KAN, Фаза 2),
          // но функционал сохранён: таб рендерится по прямой ссылке ?tab=audit
          // (см. блок {activeTab === "audit"} ниже). Вернуть в панель — просто
          // раскомментировать строку.
          // { id: "audit", label: tl("tabs.audit") },
          { id: "settings", label: tl("tabs.settings") },
        ]
      : []),
  ];

  // Завершённый турнир без ?tab= открывается на своей вкладке по умолчанию.
  // Нужно это только когда статус приезжает ПОСЛЕ монтирования (в инициализаторе
  // useState его ещё не было): иначе эффект бы просто повторял инициализатор.
  // Выбор пользователя сюда не лезет — иначе на завершённом турнире клик по
  // «Обзору» откатывался бы обратно на «Итоги» тем же эффектом (в его deps есть
  // activeTab, а ?tab= в этот момент ещё пуст).
  useEffect(() => {
    if (userPickedTabRef.current) return;
    const tab = searchParams.get("tab");
    if (tab) return;
    if (isCompleted && activeTab !== completedDefaultTab) {
      setActiveTab(completedDefaultTab);
    }
  }, [isCompleted, searchParams, activeTab, completedDefaultTab]);

  const setTab = (tabId: string) => {
    userPickedTabRef.current = true;
    setActiveTab(tabId);
    // Обновляем URL ТИХО через history.replaceState вместо router.replace:
    // router.replace в App Router триггерит soft-навигацию (ре-рендер всего
    // роута + RSC-раунд-трип при force-dynamic), из-за чего хедер с баннером
    // и аватаркой перерисовывается/переанимируется на каждый клик по табу.
    // Контент таба переключается локальным состоянием, URL нужен только для
    // шаринга/обновления страницы.
    if (selfHref && typeof window !== "undefined") {
      window.history.replaceState(null, "", `${selfHref}?tab=${tabId}`);
    }
  };

  const tabsKey = tabs.map((t) => t.id).join("|");

  // Применение ?tab= из адреса. Эффект перезапускается не только на смену
  // адреса, но и на смену СОСТАВА вкладок (tabsKey) — это нужно, чтобы таб из
  // ссылки открылся, когда он появится в списке позже: `management` после
  // загрузки профиля, `predictions` после ответа /settings/feature-flags.
  //
  // Но у того же перезапуска есть обратная сторона: после клика по табу адрес
  // пишется через history.replaceState, и useSearchParams подхватывает его лишь
  // на следующий транзишен. Если в это окно приезжают флаги, эффект читает ещё
  // СТАРЫЙ ?tab= и возвращает человека на вкладку, которую он уже покинул.
  // Поэтому явный выбор перебивается только НАСТОЯЩЕЙ сменой адреса (значение
  // ?tab= отличается от уже отработанного), а не повторным чтением того же.
  useEffect(() => {
    const tab = searchParams.get("tab");
    const urlChanged = tab !== handledUrlTabRef.current;
    handledUrlTabRef.current = tab;
    if (!tab) return;
    if (userPickedTabRef.current && !urlChanged) return;
    if (tabsKey.split("|").includes(tab)) setActiveTab(tab);
  }, [searchParams, tabsKey]);

  // Защита от «пустой страницы»: activeTab читается из URL (?tab=…) прямо в
  // инициализаторе useState, БЕЗ проверки, что такой таб вообще существует.
  // Устаревшая/битая ссылка (переименованный таб, чужой query-параметр) или
  // исчезновение таба из списка (isManager стал false, пока был открыт
  // «management») оставляли activeTab на несуществующем id — ни один из блоков
  // {activeTab === "…"} ниже не совпадал, и вся вкладка рендерилась пустой
  // (только шапка + панель табов, контента нет вообще).
  //
  // Сбрасывать можно только тогда, когда состав вкладок ОКОНЧАТЕЛЕН. Пока ответ
  // /settings/feature-flags не пришёл, все флаги считаются выключенными, и
  // вкладки за флагом в списке ЕЩЁ нет — не потому, что её не должно быть, а
  // потому что мы пока не знаем. Заход по ?tab=predictions при включённом флаге
  // в этот момент выглядел бы как «таба не существует»: сброс на «Обзор», а
  // через мгновение возврат на «Прогнозы» — видимый прыжок. Ждём ответа флагов
  // (сама страница при этом рисуется сразу, ждёт только сброс вкладки).
  useEffect(() => {
    if (flagsLoading) return;
    if (!tabsKey) return;
    if (!tabsKey.split("|").includes(activeTab)) {
      setActiveTab(isCompleted ? completedDefaultTab : "overview");
    }
  }, [tabsKey, activeTab, isCompleted, completedDefaultTab, flagsLoading]);

  // Драфт завершился в комнате → перечитываем турнир, иначе обёртка вкладки ещё
  // 20 секунд показывает «Статус драфта: Идёт» рядом с «Команды созданы».
  useEffect(() => {
    if (tournament.format !== "Draft" || !tournamentId) return;
    const onCompleted = (e: Event) => {
      const ce = e as CustomEvent<{ tournamentId?: string }>;
      if (ce?.detail?.tournamentId && ce.detail.tournamentId !== tournamentId)
        return;
      fetchTournamentById(tournamentId);
    };
    window.addEventListener("draft:completed", onCompleted);
    return () => window.removeEventListener("draft:completed", onCompleted);
  }, [tournament.format, tournamentId, fetchTournamentById]);

  const handleUpdate = () => {
    if (tournamentId) {
      fetchTournamentById(tournamentId);
    }
  };

  const organizerHref = tournament.organizer
    ? `/organizations/${tournament.organizer.url ?? tournament.organizer.id}`
    : undefined;

  return (
    <div>
      <div className="mb-4 md:mb-8">
        <div className="md:hidden mb-4">
          <Select value={activeTab} onValueChange={setTab}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tabs.map((tab) => (
                <SelectItem key={tab.id} value={tab.id}>
                  {tab.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="hidden md:block">
          <div className="w-full overflow-x-auto">
            <div className="flex flex-wrap gap-1 border-b border-border min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={`tab-${tab.id}`}
                  data-active={activeTab === tab.id ? "true" : "false"}
                  onClick={() => setTab(tab.id)}
                  className={cn(
                    "relative px-3.5 py-2.5 text-sm whitespace-nowrap transition-colors",
                    activeTab === tab.id
                      ? "text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    // Подчёркивание активного таба — трёхцветный градиент
                    // (фиолетовый→оранжевый→голубой) из макета «Страница турнира.dc.html».
                    <span
                      className="pointer-events-none absolute left-3.5 right-3.5 -bottom-px h-1 rounded-full blur-[3px] opacity-80"
                      style={{
                        background:
                          "linear-gradient(90deg, rgba(139,92,246,0), rgba(224,114,47,.55), rgba(56,189,248,0))",
                      }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Убран дубль-блок: даты/тип турнира уже показаны в TournamentDetailHeader.
              Убраны кнопки «Турнирная сетка» и «Прогнозы» — есть табы вверху страницы.
              «Прогнозы» вообще убираются из MVP (см. flags.predictions). Оставлен
              только полезный CTA: «Сообщить результат матча» для участников и
              «Результаты» для завершённых турниров. */}
          {(isCompleted || (!isManager && !isCompleted)) && (
            <div className="flex flex-wrap gap-2">
              {isCompleted && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTab("results")}
                >
                  {tl("overview.ctaResults")}
                </Button>
              )}
              {!isManager && !isCompleted && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTab("bracket")}
                  className="gap-1.5"
                >
                  <AlertCircle className="h-4 w-4" />
                  {tl("overview.reportMatchResult")}
                </Button>
              )}
            </div>
          )}
          {isManager &&
            (() => {
              const next = getTournamentNextAction(tournament);
              if (!next?.label) return null;
              return (
                <div className="rounded-2xl bg-muted/30 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setTab("management")}
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    {tl("overview.nextStepLabel", { action: next.label })}
                  </button>
                </div>
              );
            })()}

          <div
            className="relative overflow-hidden rounded-[22px] border px-5 sm:px-6 py-5"
            style={{
              borderColor:
                "color-mix(in srgb, var(--primary) 30%, var(--border))",
              background:
                "linear-gradient(155deg, color-mix(in srgb, var(--primary) 12%, var(--card)), var(--card) 60%)",
            }}
          >
            <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-foreground">
              <AlertCircle className="h-4 w-4 text-primary" />
              {tl("overview.aboutTitle")}
            </h2>
            {tournament.description ? (
              <p className="text-foreground text-sm leading-relaxed whitespace-pre-wrap mb-4">
                {tournament.description}
              </p>
            ) : null}
            <div className="text-xs text-muted-foreground">
              {tl("overview.organizer")}
            </div>
            {tournament.organizer && organizerHref ? (
              <Link
                href={organizerHref}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity w-fit mt-1"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={tournament.organizer.avatar}
                    alt={tournament.organizer.name}
                  />
                  <AvatarFallback className="text-sm">
                    {tournament.organizer.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm text-foreground">
                  {tournament.organizer.name}
                </span>
              </Link>
            ) : (
              <span className="text-muted-foreground text-sm">—</span>
            )}
            <TournamentAboutModeratorsBlock tournamentId={tournamentId} />
          </div>

          {/* Официальная трансляция — рендерится только если настройка ShowOfficialStream
              включена (KAN-91, дефолт true) и у турнира задан Twitch-канал. Сам блок
              дополнительно скрывает себя, если канал сейчас не в эфире (KAN-92). */}
          {(() => {
            if (tournament.showOfficialStream === false) return null;
            const officialStreamChannel =
              tournament.officialStreamChannel?.trim();
            return officialStreamChannel ? (
              <TournamentOfficialStream channel={officialStreamChannel} />
            ) : null;
          })()}

          <div className="rounded-[22px] border border-border bg-card shadow-sm px-5 sm:px-6 py-5">
            <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" />
              {tl("overview.conditionsTitle")}
            </h2>
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
              <div className="rounded-xl border border-border bg-card px-4 py-4 text-center">
                {tournament.type === "Draft" ? (
                  // Драфт: участники в пул + отдельно число команд (не «1/0»).
                  <>
                    <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                      <Users className="h-3.5 w-3.5 text-primary" />
                      {tUi("tournamentDetailTabs.uchastniki")}
                    </div>
                    <div className="mt-1.5 text-2xl font-bold text-foreground">
                      {tournament.registeredPlayersCount ?? 0}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {tournament.maxTeamsInDraft ?? "—"} команд
                    </div>
                  </>
                ) : isArena ? (
                  // KAN-1645: в DTO Arena currentTeams = RegisteredPlayersCount (число
                  // регистраций), а maxTeams = MaxPlayers (лимит ИГРОКОВ) — в карточке
                  // получалось «Команды 3/9». Единицы числителя/знаменателя должны
                  // совпадать: PreMade-регистрация — это команда целиком (лимит команд —
                  // arenaTeamCount), Random-регистрация — соло-игрок (лимит — MaxPlayers).
                  tournament.arenaTeamMode === "Random" ? (
                    <>
                      <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                        <Users className="h-3.5 w-3.5 text-primary" />
                        {tUi("tournamentDetailTabs.uchastniki")}
                      </div>
                      <div className="mt-1.5 text-2xl font-bold text-foreground">{`${currentTeams}/${maxTeams}`}</div>
                      <Progress
                        value={
                          maxTeams > 0 ? (currentTeams / maxTeams) * 100 : 0
                        }
                        className="mt-2 h-1.5"
                      />
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                        <Users className="h-3.5 w-3.5 text-primary" />
                        {tl("overview.teams")}
                      </div>
                      <div className="mt-1.5 text-2xl font-bold text-foreground">{`${currentTeams}/${tournament.arenaTeamCount ?? maxTeams}`}</div>
                      <Progress
                        value={
                          (tournament.arenaTeamCount ?? 0) > 0
                            ? (currentTeams / (tournament.arenaTeamCount ?? 1)) *
                              100
                            : 0
                        }
                        className="mt-2 h-1.5"
                      />
                    </>
                  )
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                      <Users className="h-3.5 w-3.5 text-primary" />
                      {tl("overview.teams")}
                    </div>
                    <div className="mt-1.5 text-2xl font-bold text-foreground">{`${currentTeams}/${maxTeams}`}</div>
                    <Progress
                      value={maxTeams > 0 ? (currentTeams / maxTeams) * 100 : 0}
                      className="mt-2 h-1.5"
                    />
                  </>
                )}
              </div>
              <div className="rounded-xl border border-border bg-card px-4 py-4 text-center">
                <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  <DollarSign className="h-3.5 w-3.5 text-primary" />
                  {tl("overview.prizePool")}
                </div>
                <div className="mt-1.5 text-2xl font-bold text-foreground">
                  {displayPrizeText}
                </div>
              </div>
              {minRequiredRankRu ? (
                <div className="rounded-xl border border-border bg-card px-4 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                    {tl("overview.minRank")}
                  </div>
                  <div className="mt-1.5 text-2xl font-bold text-foreground">
                    {minRequiredRankRu}
                  </div>
                </div>
              ) : null}
              {(() => {
                const bo = Number(tournament.defaultBestOf ?? 1);
                const hasBo = bo && bo >= 1;
                return (
                  <div className="rounded-xl border border-border bg-card px-4 py-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                      <Swords className="h-3.5 w-3.5 text-primary" />
                      {hasBo
                        ? `${tl("overview.format")} / ${tl("overview.series")}`
                        : tl("overview.format")}
                    </div>
                    <div className="mt-1.5 text-2xl font-bold text-foreground">
                      {hasBo
                        ? `${tournament.format} · ${tl("overview.seriesBo", { n: bo })}`
                        : tournament.format}
                    </div>
                  </div>
                );
              })()}
              <div className="rounded-xl border border-border bg-card px-4 py-4 text-center">
                <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5 text-primary" />
                  {tl("overview.dates")}
                </div>
                <div className="mt-1.5 whitespace-nowrap text-xl font-bold text-foreground">
                  {startDate
                    ? `${formatDateOnlyMsk(startDate)} – ${formatDateOnlyMsk(endDate ?? startDate)}`
                    : "—"}
                </div>
              </div>
            </div>
          </div>

          {((
            tournament as { prizeDescription?: string | null }
          ).prizeDescription?.trim() ||
            (prizeDistribution?.length ?? 0) > 0) && (
            <div className="rounded-[22px] border border-border bg-card shadow-sm px-5 sm:px-6 py-5 space-y-4">
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <DollarSign className="h-4 w-4 text-primary" />
                {tl("overview.prizesTitle")}
              </h2>
              {(
                tournament as { prizeDescription?: string | null }
              ).prizeDescription?.trim() && (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {
                    (tournament as { prizeDescription: string })
                      .prizeDescription
                  }
                </p>
              )}
              {(prizeDistribution?.length ?? 0) > 0 && (
                <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
                  {(prizeDistribution ?? []).map((row, i) => {
                    const placeLabel =
                      row.placeFrom === row.placeTo
                        ? tUi("litTournamentDetailTabs.mesto", { place: row.placeFrom })
                        : tUi("litTournamentDetailTabs.mestoRange", { from: row.placeFrom, to: row.placeTo });
                    const bigValue =
                      row.sharePercent != null
                        ? `${row.sharePercent}%`
                        : row.quantity != null
                          ? String(row.quantity)
                          : "—";
                    const subLabel =
                      row.prizeType === "Money"
                        ? tUi("litTournamentDetailTabs.otPrizovogoFonda")
                        : row.prizeType === "InGame"
                          ? (row.description ?? tUi("litTournamentDetailTabs.vnutriigrovoyPriz"))
                          : (row.description ?? tUi("litTournamentDetailTabs.fizicheskiyPriz"));
                    const medal =
                      PRIZE_MEDALS[String(row.placeFrom)] ??
                      DEFAULT_PRIZE_MEDAL;
                    return (
                      <div
                        key={i}
                        className="relative overflow-hidden rounded-[14px] border px-4 py-[18px] text-center"
                        style={{
                          borderColor: medal.border,
                          background: medal.bg,
                        }}
                      >
                        <div
                          className="text-[22px]"
                          style={{
                            fontFamily: "var(--font-logo)",
                            background: medal.text,
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            color: "transparent",
                          }}
                        >
                          {placeLabel}
                        </div>
                        <div
                          className="mt-2 text-[28px] font-bold text-foreground"
                          style={{ fontFamily: "var(--font-heading)" }}
                        >
                          {bigValue}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {subLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {showRulesCard && (
            <div className="rounded-[22px] border border-border bg-card shadow-sm px-5 sm:px-6 py-5 pb-8 space-y-4">
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <ShieldCheck className="h-4 w-4 text-primary" />
                {tl("overview.rulesTitle")}
              </h2>
              {/* KAN-665: регламент, написанный организатором в настройках турнира. */}
              <TournamentRulesText text={tournamentRulesText} />
              {isSolo1v1 && (
                <div className="space-y-4 rounded-2xl border border-border/60 bg-background/40 px-4 py-4">
                  <h3 className="font-semibold text-base text-foreground">
                    {tl("overview.rules1v1Title")}
                  </h3>

                  <div className="space-y-1">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {tl("overview.rules1v1MapHeading")}
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {tl("overview.rules1v1Map")}
                    </p>
                    <p className="text-sm text-foreground">
                      {tl("overview.rules1v1Mode")}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {tl("overview.rules1v1BansHeading")}
                    </div>
                    <p className="text-sm text-foreground">
                      {tl("overview.rules1v1BansDesc")}
                    </p>
                    <ol className="text-sm space-y-1 pl-4 list-decimal text-foreground/90 marker:text-primary marker:font-semibold">
                      <li>{tl("overview.rules1v1BansStep1")}</li>
                      <li>{tl("overview.rules1v1BansStep2")}</li>
                      <li>{tl("overview.rules1v1BansStep3")}</li>
                      <li>{tl("overview.rules1v1BansStep4")}</li>
                    </ol>
                  </div>

                  <div className="space-y-1">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {tl("overview.rules1v1RuneHeading")}
                    </div>
                    <p className="text-sm text-foreground">
                      {tl("overview.rules1v1RuneDemolish")}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {tl("overview.rules1v1WinHeading")}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {tl("overview.rules1v1Desc")}
                    </p>
                    <ul className="text-sm space-y-1 pl-4 list-disc text-foreground/90 marker:text-primary">
                      <li>{tl("overview.rules1v1Kill")}</li>
                      <li>{tl("overview.rules1v1Cs")}</li>
                      <li>{tl("overview.rules1v1Tower")}</li>
                    </ul>
                  </div>

                  <p className="text-xs text-muted-foreground border-t border-border/40 pt-2">
                    {tl("overview.rules1v1Auto")}
                  </p>
                </div>
              )}
              {flags.rulebook && (
                <TournamentRulebookOverviewBlock tournamentId={tournamentId} />
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "results" && (
        <WidgetBoundary name="tournament-results-tab">
          <TournamentResultsTab
            tournamentId={tournamentId}
            tournamentRegion={tournament.region}
          />
        </WidgetBoundary>
      )}

      {activeTab === "draft" && tournament.format === "Draft" && (
        <Card>
          <CardHeader>
            <CardTitle>{tl("draftTab.title")}</CardTitle>
            <CardDescription>{tl("draftTab.description")}</CardDescription>
            {/* Слот для кнопок драфт-комнаты (заявки/войс/управление): рисуются
                порталом из DraftHeader, чтобы стоять в одну строку с описанием. */}
            <CardAction id={DRAFT_ACTIONS_SLOT_ID} />
          </CardHeader>
          <CardContent>
            {(() => {
              const draftStartAt =
                tournament.draftStartAt || tournament.draftStartTime;
              const draftStartedAt = tournament.draftStartedAt;
              const isDraftStarted = Boolean(tournament.isDraftStarted);
              const isDraftCompleted = Boolean(tournament.isDraftCompleted);
              const isDraftBlocked = Boolean(tournament.isDraftBlocked);
              const draftBlockedReason =
                tournament.draftBlockedReason ?? undefined;

              const statusText = isDraftCompleted
                ? tl("draftTab.statusCompleted")
                : isDraftStarted
                  ? tl("draftTab.statusActive")
                  : isDraftBlocked
                    ? tl("draftTab.statusBlocked")
                    : tl("draftTab.statusPending");

              return (
                <div className="space-y-4">
                  {/* Трансляция драфта: если организатор стримит и канал в эфире —
                      плеер над комнатой. Оффлайн — блока нет (компонент сам скрыт). */}
                  {tournament.showOfficialStream !== false &&
                  tournament.officialStreamChannel?.trim() ? (
                    <TournamentOfficialStream
                      channel={tournament.officialStreamChannel.trim()}
                    />
                  ) : null}

                  {/* Завершённый драфт: статус/время/чек-ин уже неактуальны —
                      показываем сразу комнату с итоговыми составами. */}
                  {!isDraftCompleted && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-lg border p-3">
                        <div className="text-xs text-muted-foreground">
                          {tl("draftTab.statusLabel")}
                        </div>
                        <div className="text-base font-semibold">
                          {statusText}
                        </div>
                        {isDraftBlocked && draftBlockedReason ? (
                          <div className="text-xs text-muted-foreground mt-1">
                            {draftBlockedReason}
                          </div>
                        ) : null}
                      </div>
                      <div className="rounded-lg border p-3">
                        <div className="text-xs text-muted-foreground">
                          {tl("draftTab.startLabel", {
                            timezone: MSK_TIME_ZONE,
                          })}
                        </div>
                        <div className="text-base font-semibold">
                          {draftStartAt
                            ? `${formatDateOnlyMsk(draftStartAt, {
                                day: "numeric",
                                month: "long",
                              })}, ${formatTimeOnlyMsk(draftStartAt, {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`
                            : "—"}
                        </div>
                        {/* Человеческая подсказка вместо голой даты: «завтра», «через 3 дня». */}
                        {draftStartAt ? (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {formatDraftStartHint(draftStartAt)}
                          </div>
                        ) : null}
                        {draftStartedAt ? (
                          <div className="text-xs text-muted-foreground mt-1">
                            {tl("draftTab.startedAt", {
                              date: formatDateMsk(draftStartedAt),
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {/* Про чек-ин пишем только если он реально включён у турнира. */}
                  {!isDraftCompleted && tournament.enableCheckIn && (
                    <div className="rounded-lg border p-3 text-sm text-muted-foreground">
                      {tl("draftTab.checkInNotice")}
                    </div>
                  )}

                  <div>
                    <DraftRoom tournamentId={tournamentId} />
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {activeTab === "streamers" && (
        <WidgetBoundary name="tournament-streamers-tab">
          <TournamentStreamersTab
            tournamentId={tournamentId}
            officialChannel={tournament.officialStreamChannel}
            showOfficialStream={tournament.showOfficialStream !== false}
          />
        </WidgetBoundary>
      )}

      {activeTab === "teams" && tournament.arenaTeamMode === "Random" && (
        // Arena Random: «Команды» — основной surface составов. Управление составами
        // (посадить/убрать/заменить/инвайт/расформировать) доступно менеджеру И до старта
        // (phase=preview: + пересборка/подтвердить), И во время турнира (phase=ongoing).
        // После завершения/отмены — только просмотр. Обычный игрок всегда только смотрит.
        <ArenaRostersPanel
          tournamentId={tournamentId}
          isManager={
            isManager &&
            arenaBackendStatus !== "Completed" &&
            arenaBackendStatus !== "Cancelled"
          }
          lobbyCount={tournament.arenaLobbyCount ?? 1}
          phase={arenaRunning ? "ongoing" : "preview"}
        />
      )}

      {activeTab === "teams" && tournament.arenaTeamMode !== "Random" && (
        <div className="rounded-[14px] border border-border bg-card shadow-sm p-4 md:p-6">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-xl font-bold text-foreground">
                <Users className="h-5 w-5 text-primary" />
                {tournament.format === "Draft"
                  ? tl("teamsTab.titleParticipants")
                  : tournament.format === "SoloOneVsOne"
                    ? tl("teamsTab.titleParticipants")
                    : tl("teamsTab.titleTeams")}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {tournament.format === "Draft"
                  ? tl("teamsTab.descParticipants")
                  : tournament.format === "SoloOneVsOne"
                    ? tl("teamsTab.descSolo")
                    : tl("teamsTab.descClassic")}
              </p>
            </div>

            {tournament.format === "Draft" && (
              <DraftBudgetBadge
                budget={tournament.draftCaptainBudget ?? undefined}
              />
            )}
          </div>
          <div>
            <div className="space-y-4">
              {tournament.format === "Draft" ? (
                <>
                  {/* Модерация переехала в сам список: у организатора неподтверждённые
                    заявки видны строками с бейджем и кнопками «Принять/Отклонить». */}
                  <TournamentParticipantsGrid
                    tournamentId={tournamentId}
                    teamsNeeded={tournament.maxTeamsInDraft ?? undefined}
                    isManager={isManager}
                    isCaptain={isDraftCaptain}
                    draftCompleted={Boolean(tournament.isDraftCompleted)}
                    captainBudget={
                      (
                        tournament as {
                          draftCaptainBudget?: number;
                          draftBudget?: number;
                        }
                      ).draftCaptainBudget ??
                      (tournament as { draftBudget?: number }).draftBudget
                    }
                    draftSettingsHref={`/tournaments/${tournamentId}/draft-settings`}
                  />
                </>
              ) : (
                <>
                  <TournamentTempTeamsPanel tournament={tournament} />
                  <TournamentParticipantsTeamsGrid
                    tournamentId={tournamentId}
                    tournamentFormat={tournamentFormat}
                    hideTeamsSummary={
                      tournament.format === "Classic" &&
                      tournament.maxTeamSize === 1
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "bracket" && (
        <WidgetBoundary name="tournament-bracket-tab">
          <TournamentBracketTab tournamentId={tournamentId} />
        </WidgetBoundary>
      )}

      {activeTab === "arena" &&
        (tournament.arenaTeamMode === "Random" ? (
          // Arena Random: этот таб — только лидерборд с баллами (составами рулят
          // в «Командах»). Очки редактируются только пока турнир идёт.
          <ArenaLeaderboard
            tournamentId={tournamentId}
            isManager={isManager && arenaRunning}
            isCompleted={isCompleted}
          />
        ) : (
          <ArenaPanel tournamentId={tournamentId} isManager={isManager} />
        ))}

      {activeTab === "quests" && isQuestTournament && (
        <div className="overflow-hidden rounded-xl border bg-card">
          <iframe
            src="https://baddie-arena-quests.netlify.app/"
            title={tUi("tournamentDetailTabs.kvestyTurnira")}
            className="h-[78vh] w-full"
            loading="lazy"
            sandbox="allow-scripts allow-popups allow-forms allow-same-origin"
            referrerPolicy="no-referrer"
          />
        </div>
      )}

      {activeTab === "predictions" && flags.predictions && (
        <TournamentPredictionsScreen
          tournamentIdOrSlug={tournamentId}
          tournamentName={tournament.name ?? tournamentId}
          backHref={selfHref ?? `/tournaments/${tournamentId}`}
          embedded
        />
      )}

      {activeTab === "schedule" && (
        <WidgetBoundary name="tournament-schedule-tab">
          <TournamentScheduleTab tournament={tournament} />
        </WidgetBoundary>
      )}

      {activeTab === "reviews-complaints" &&
        user &&
        flags.reviews_complaints && (
          <TournamentReviewsComplaintsTab tournamentId={tournamentId} />
        )}

      {activeTab === "management" && isManager && (
        <TournamentManagementTab
          tournament={tournament}
          onUpdate={handleUpdate}
        />
      )}

      {activeTab === "audit" && isManager && (
        <TournamentAuditTab tournamentId={tournamentId} />
      )}

      {activeTab === "settings" && isManager && (
        <TournamentSettingsTab
          tournament={tournament}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
};
